import {Breadcrumb} from 'components/DSYS/Breadcrumb';
import {useDcdOwnershipService} from 'components/teams/dcdOwnership';
import {
  useDcdOwnershipComponent,
  useDcdOwnershipComponentReference,
  useDcdOwnershipComponents,
} from 'components/teams/hooks';
import {OwnershipBreadcrumb} from 'components/teams/OwnershipBreadcrumb';
import {OwnershipComponentList} from 'components/teams/OwnershipComponentList';
import {OwnershipDetail} from 'components/teams/OwnershipDetail';
import {findDcdOwnershipService, parseIdParam} from 'components/teams/ownershipHelpers';
import {TeamOwnershipDirectoryLayout} from 'components/teams/TeamOwnershipDirectoryLayout';
import {getComponentsAndServiceReferenceRoute, getDefaultTeamsRoute} from 'constant';
import {useDebouncedValue} from 'hooks/useDebounce';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {Suspense, useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router';

export const TeamOwnershipView = () => {
  const {
    componentId: componentIdParam,
    serviceId: serviceIdParam,
    referenceId: referenceIdParam,
  } = useParams<{
    componentId?: string;
    serviceId?: string;
    referenceId?: string;
  }>();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300) as string;
  const searchQuery = debouncedSearch.trim();
  const useServerSearch =
    searchQuery.length >= 3 && /[/._]/.test(searchQuery) && !/\s/.test(searchQuery);
  const serverSearchQuery = useServerSearch ? searchQuery : undefined;
  const {data: components = [], isLoading, isError} = useDcdOwnershipComponents(serverSearchQuery);

  const parsedComponentId = useMemo(() => parseIdParam(componentIdParam), [componentIdParam]);
  const parsedServiceId = useMemo(() => parseIdParam(serviceIdParam), [serviceIdParam]);
  const parsedReferenceId = useMemo(() => parseIdParam(referenceIdParam), [referenceIdParam]);
  const isServiceRoute = serviceIdParam !== undefined;
  const isReferenceRoute = parsedReferenceId !== undefined;

  const selectedComponentLeftNavSummary = useMemo(() => {
    if (!components.length || parsedComponentId === undefined) {
      return undefined;
    }
    return components.find(({id}) => id === parsedComponentId);
  }, [components, parsedComponentId]);
  const selectedComponentId =
    useServerSearch || isServiceRoute || isReferenceRoute
      ? parsedComponentId
      : selectedComponentLeftNavSummary?.id;

  const {
    data: selectedComponentDetail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useDcdOwnershipComponent(selectedComponentId);
  const {
    data: selectedReferenceDetail,
    isLoading: isReferenceLoading,
    isError: isReferenceError,
  } = useDcdOwnershipComponentReference(parsedComponentId, parsedReferenceId);
  const selectedComponentService = isServiceRoute
    ? findDcdOwnershipService(selectedComponentDetail?.services, parsedServiceId)
    : undefined;
  const {
    data: selectedService,
    isLoading: isServiceLoading,
    isError: isServiceError,
  } = useDcdOwnershipService(selectedComponentService?.id);
  const isOwnershipDetailLoading =
    selectedComponentId !== undefined &&
    (isDetailLoading ||
      (isReferenceRoute && isReferenceLoading) ||
      (isServiceRoute && selectedComponentService !== undefined && isServiceLoading));
  const hasInvalidServiceURL =
    isServiceRoute &&
    selectedComponentDetail !== undefined &&
    selectedComponentService === undefined;
  const hasReferenceError = isReferenceRoute && isReferenceError;
  const hasOwnershipDetailError =
    isDetailError || hasReferenceError || isServiceError || hasInvalidServiceURL;
  const isServiceDetailReady = isServiceRoute ? selectedService !== undefined : true;

  useDocumentTitle(
    selectedReferenceDetail?.name ??
      selectedService?.name ??
      selectedComponentService?.name ??
      selectedComponentDetail?.name ??
      selectedComponentLeftNavSummary?.name ??
      t('teams_components_and_services').toString()
  );

  useEffect(() => {
    if (useServerSearch || isLoading || isError || !components.length) {
      return;
    }

    if (parsedComponentId === undefined) {
      navigate(getComponentsAndServiceReferenceRoute(components[0].id), {replace: true});
      return;
    }

    if (!components.some(({id}) => id === parsedComponentId)) {
      navigate(getComponentsAndServiceReferenceRoute(components[0].id), {replace: true});
    }
  }, [components, isError, isLoading, navigate, parsedComponentId, useServerSearch]);

  return (
    <TeamOwnershipDirectoryLayout
      activeSection="components-and-services"
      search={search}
      searchPlaceholder={t('ownership_search').toString()}
      searchResetKey={searchQuery}
      onSearchChange={setSearch}
      detailLoadingVariant={
        isOwnershipDetailLoading
          ? 'detail'
          : isLoading && !selectedComponentDetail && !hasOwnershipDetailError
            ? 'directory'
            : undefined
      }
      isDirectoryEmpty={!components.length}
      isDirectoryError={isError}
      isDirectoryLoading={isLoading}
      isDetailError={hasOwnershipDetailError}
      directory={(bleed) => (
        <OwnershipComponentList
          bleed={bleed}
          components={components}
          searchQuery={searchQuery}
          useServerSearch={useServerSearch}
          showServerSearchMatches={useServerSearch}
          isSearchLoading={isLoading}
          isSearchError={isError}
          activeComponentId={parsedComponentId}
          onNavigate={() => setSearch('')}
        />
      )}
      breadcrumb={
        <Suspense
          fallback={
            <Breadcrumb
              path={[
                {children: t('teams'), to: getDefaultTeamsRoute()},
                {
                  children: t('teams_components_and_services'),
                  to: getComponentsAndServiceReferenceRoute(),
                },
              ]}
            />
          }
        >
          <OwnershipBreadcrumb />
        </Suspense>
      }
    >
      {selectedComponentDetail && isServiceDetailReady ? (
        <OwnershipDetail
          component={selectedComponentDetail}
          title={selectedReferenceDetail?.name ?? selectedService?.name}
          description={
            isReferenceRoute
              ? undefined
              : isServiceRoute
                ? selectedService?.description
                : selectedComponentDetail.description
          }
          selectedService={selectedService}
          selectedReference={isReferenceRoute ? selectedReferenceDetail : undefined}
          showServices={!isReferenceRoute && !isServiceRoute}
        />
      ) : null}
    </TeamOwnershipDirectoryLayout>
  );
};
